/**
 * SuccessorCard for the recording flow.
 * Renders existing successors and opens a single merged modal that combines
 * type choice + entity selection (or unclear/none confirmation).
 *
 * Unclear/None payloads come from `toSuccessorUpdate({ type })`, which writes
 * empty arrays for all four collections alongside successorStatus — defeating
 * the autosave merger that does `data.x ?? existing.x`.
 *
 * `orgFunctions` and `systems` are `EntityOrSuggestionRef[]`. Each entry is either
 * an `{ type: 'entity', id }` ref (resolved against `orgFunctionHashById`/`systemHashById`)
 * or a `{ type: 'suggestion', id }` ref (resolved against the recording's
 * `entitySuggestions[]` and rendered as a pending/rejected chip).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { keyBy } from 'lodash';
import { Plus, X } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { type EntityOrSuggestionRef, type EntitySuggestion, type User } from '@bliks-io/contracts';
import { useSystemsForLookup } from '@/api/queries/useSystems';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import { useExternalContactsForLookup } from '@/api/queries/useExternalContacts';
import { usePrefetchPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { getEntityRefIds } from '@/lib/entityRefUtils';
import { Card, CardHeader, CardContent, Button, InfoHint, cn } from '@bliks-io/ui';
import {
  PersonSuccessorItem,
  TeamSuccessorItem,
  SystemSuccessorItem,
  ExternalContactSuccessorItem,
  StatusSuccessorItem,
  GhostSuccessorItem
} from './successor-items';
import { SuccessorListItem } from './SuccessorListItem';
import { SuccessorEntityPickerModal } from '@/components/entity-pickers';
import type { SuccessorChangeHandler, SuccessorUpdateData } from '../../types';

export type SuccessorCardType = 'task' | 'decision' | 'assignee' | 'starter';

type Props = {
  users: User[];
  submitted: boolean;
  value: Required<SuccessorUpdateData>;
  onChange: SuccessorChangeHandler;
  type?: SuccessorCardType;
  /** sourceStepRecordingId; required for the system-request flow on this card. */
  recordingId?: string;
  /** Process this card belongs to — drives the "for this process" picker bucket. */
  processId?: string;
  /**
   * Start event node behind the starter card (issue 11): suggestions created
   * from the picker anchor to this node (`sourceRecordingId`).
   */
  startEventNodeId?: string;
  /** Draft host: suggestions post to the draft routes (see SuccessorEntityPickerModal). */
  draftProcessId?: string;
  /** Create teams / org functions / systems outright instead of suggesting them. */
  allowCreate?: boolean;
  /** Optional rule id for decision-rule successors. */
  sourceRuleId?: string;
  /**
   * Recording entity suggestions. Suggestion-ref entries in `value.orgFunctions` /
   * `value.systems` are looked up here so we can render a pending or rejected
   * chip alongside resolved entity rows.
   */
  entitySuggestions?: EntitySuggestion[];
};

const getProcessEndLabels = (t: (key: string) => string) => ({
  UNCLEAR: { label: t('viralRecording.labels.unclear') },
  NONE: { label: t('viralRecording.labels.none') }
});

// Pure check kept outside the component so each `&&` doesn't count against
// SuccessorCard's cyclomatic complexity budget.
function hasNoSuccessorValue(value: Required<SuccessorUpdateData>): boolean {
  return (
    (value?.persons?.length ?? 0) === 0 &&
    (value?.orgFunctions?.length ?? 0) === 0 &&
    (value?.systems?.length ?? 0) === 0 &&
    (value?.externalContacts?.length ?? 0) === 0 &&
    !value.successorStatus
  );
}

function resolveModalMode(
  type: SuccessorCardType | undefined
): 'successor' | 'assignee' | 'starter' {
  switch (type) {
    case 'starter':
      return 'starter';
    case 'assignee':
      return 'assignee';
    default:
      return 'successor';
  }
}

function resolveDescriptionKey(type: SuccessorCardType | undefined): string {
  switch (type) {
    case 'starter':
      return 'processDraft.startEvent.assigneeCardSubtitle';
    case 'assignee':
      return 'processPage.chooseAllAssignee';
    case 'decision':
      return 'processPage.chooseAllSuccessorDecision';
    default:
      return 'processPage.chooseAllSuccessor';
  }
}

// Empty-state helper text under the card illustration. The start event gets a
// dedicated line that explains what "weiterarbeitet" with the start event means.
function resolveEmptyHelperKey(type: SuccessorCardType | undefined): string {
  switch (type) {
    case 'starter':
      return 'processDraft.startEvent.assigneeCardHelper';
    case 'assignee':
      return 'processPage.anAssigneeCanBe';
    default:
      return 'processPage.aSuccessorCanBe';
  }
}

function SuccessorCard(props: Props) {
  const { t } = useTranslation();
  const PROCESS_END = getProcessEndLabels(t);
  const modalMode = resolveModalMode(props.type);
  const isAssigneeMode = modalMode !== 'successor';

  const [modalOpen, setModalOpen] = useState(false);

  // Lookup variants include soft-deleted entities so refs on old recordings
  // still resolve; the successor items render those grayed with "(deleted)".
  // The picker modal builds its own option lists and never sees deleted ones.
  const { data: softwares = [] } = useSystemsForLookup();
  const { data: orgFunctions = [] } = useOrgFunctionsForLookup();
  const { data: externalContacts = [] } = useExternalContactsForLookup();

  usePrefetchPickerSuggestions(['user'], {
    processId: props.processId,
    recordingId: props.recordingId
  });

  const userHashById = useMemo(() => keyBy(props.users, '_id'), [props.users]);
  const systemHashById = useMemo(() => keyBy(softwares, '_id'), [softwares]);
  const orgFunctionHashById = useMemo(() => keyBy(orgFunctions, '_id'), [orgFunctions]);
  const externalContactHashById = useMemo(() => keyBy(externalContacts, '_id'), [externalContacts]);
  const suggestionById = useMemo(
    () => keyBy(props.entitySuggestions ?? [], '_id'),
    [props.entitySuggestions]
  );

  const handleRemovePerson = (id: string) => {
    void props.onChange({
      persons: props.value.persons.filter((item) => item !== id)
    });
  };

  const handleRemoveExternalContact = (id: string) => {
    void props.onChange({
      externalContacts: props.value.externalContacts.filter((item) => item !== id)
    });
  };

  const handleRemoveTeamRef = (refId: string) => {
    void props.onChange({
      orgFunctions: props.value.orgFunctions.filter((entry) => entry.id !== refId)
    });
  };

  const handleRemoveSystemRef = (refId: string) => {
    void props.onChange({
      systems: props.value.systems.filter((entry) => entry.id !== refId)
    });
  };

  const hasNoSuccessors = hasNoSuccessorValue(props.value);
  const validate = {
    successor: hasNoSuccessors ? 'processPage.successorRequired' : ''
  };

  // Modal emits `{ persons: [id], successorStatus: '' }` for an entity pick;
  // append to the existing list so successive picks accumulate. Unclear/none
  // payloads include empty arrays for all collections, so passing them
  // through replaces (which is the intended reset).
  const handleModalSave = (update: SuccessorUpdateData) => {
    const isStatusReset = update.successorStatus === 'UNCLEAR' || update.successorStatus === 'NONE';
    if (isStatusReset) {
      void props.onChange(update);
      return;
    }
    const merged: SuccessorUpdateData = { successorStatus: '' };
    if (update.persons?.[0]) merged.persons = [...(props.value.persons ?? []), update.persons[0]];
    if (update.orgFunctions?.[0])
      merged.orgFunctions = [...(props.value.orgFunctions ?? []), update.orgFunctions[0]];
    if (update.systems?.[0]) merged.systems = [...(props.value.systems ?? []), update.systems[0]];
    if (update.externalContacts?.[0])
      merged.externalContacts = [
        ...(props.value.externalContacts ?? []),
        update.externalContacts[0]
      ];
    void props.onChange(merged);
  };

  // Render an EntityOrSuggestionRef entry: looks up the resolved entity (or
  // suggestion) and wraps it in a SuccessorListItem. Common to orgFunctions/systems.
  const renderRefEntry = <E extends { _id: string }>(
    entry: EntityOrSuggestionRef,
    entityById: Record<string, E>,
    renderEntity: (entity: E) => ReactNode,
    onRemove: (refId: string) => void
  ): ReactNode => {
    if (entry.type === 'suggestion') {
      const suggestion = suggestionById[entry.id];
      if (!suggestion) return null;
      return (
        <SuccessorListItem
          key={`suggestion-${entry.id}`}
          id={entry.id}
          onRemove={() => onRemove(entry.id)}
        >
          <GhostSuccessorItem suggestion={suggestion} />
        </SuccessorListItem>
      );
    }
    const entity = entityById[entry.id];
    if (!entity) return null;
    return (
      <SuccessorListItem key={entry.id} id={entry.id} onRemove={() => onRemove(entry.id)}>
        {renderEntity(entity)}
      </SuccessorListItem>
    );
  };

  return (
    <>
      <Card
        className={cn(
          'nopan w-full! max-w-[424px] min-w-[350px]',
          props.submitted && validate.successor && 'border-destructive'
        )}
      >
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <ENTITY_ICONS.successor className="h-5 w-5" />
            <div className="text-lg font-semibold">
              {t(isAssigneeMode ? 'processPage.Assignee' : 'processPage.Successor')}
              <span className="text-destructive ml-1">*</span>
            </div>
            {props.type === 'starter' && (
              <InfoHint content={t('processDraft.startEvent.titleHint')} side="bottom" />
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {t(resolveDescriptionKey(props.type))}
          </div>

          {validate.successor && props.submitted && (
            <div className="text-sm text-destructive">{t(validate.successor)}</div>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {hasNoSuccessors && (
            <div className="flex flex-col items-center gap-2 py-4">
              <img
                className="w-[116px] h-[116px]"
                src="/assets/images/same-day.png"
                alt="same-day"
              />
              <div className="text-muted-foreground text-xs text-center">
                {t(resolveEmptyHelperKey(props.type))}
              </div>
            </div>
          )}

          {(props.value?.persons || []).map((userId) => {
            const person = userHashById[userId];
            if (!person) return null;
            return (
              <SuccessorListItem
                key={userId}
                id={userId}
                onRemove={() => handleRemovePerson(userId)}
              >
                <PersonSuccessorItem user={person} />
              </SuccessorListItem>
            );
          })}

          {(props.value?.orgFunctions || []).map((entry) =>
            renderRefEntry(
              entry,
              orgFunctionHashById,
              (team) => <TeamSuccessorItem team={team} />,
              handleRemoveTeamRef
            )
          )}

          {(props.value?.systems || []).map((entry) =>
            renderRefEntry(
              entry,
              systemHashById,
              (system) => <SystemSuccessorItem system={system} />,
              handleRemoveSystemRef
            )
          )}

          {(props.value?.externalContacts || []).map((contactId) => {
            const contact = externalContactHashById[contactId];
            if (!contact) return null;
            return (
              <SuccessorListItem
                key={contactId}
                id={contactId}
                onRemove={() => handleRemoveExternalContact(contactId)}
              >
                <ExternalContactSuccessorItem contact={contact} />
              </SuccessorListItem>
            );
          })}

          {props.value?.successorStatus && (
            <div className="relative">
              <StatusSuccessorItem
                status={props.value.successorStatus as 'UNCLEAR' | 'NONE'}
                label={PROCESS_END[props.value.successorStatus as keyof typeof PROCESS_END]?.label}
                description={t('viralRecording.labels.theProcessWillEndHere')}
              />
              <button
                type="button"
                onClick={() => props.onChange({ successorStatus: '' })}
                className="absolute top-1/2 right-2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full hover:bg-accent"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {!props.value.successorStatus && (
            <Button onClick={() => setModalOpen(true)} className="w-full" variant="outline">
              {t(isAssigneeMode ? 'processPage.addAssignee' : 'processPage.addSuccessor')}
              <Plus className="ml-2 h-4 w-4" />
            </Button>
          )}
        </CardContent>
      </Card>

      <SuccessorEntityPickerModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSave={handleModalSave}
        processId={props.processId}
        recordingId={props.recordingId}
        startEventNodeId={props.startEventNodeId}
        draftProcessId={props.draftProcessId}
        allowCreate={props.allowCreate}
        sourceRuleId={props.sourceRuleId}
        mode={modalMode}
        excludeIds={{
          persons: props.value.persons ?? [],
          orgFunctions: getEntityRefIds(props.value.orgFunctions),
          systems: getEntityRefIds(props.value.systems),
          externalContacts: props.value.externalContacts ?? []
        }}
        pendingSuggestions={props.entitySuggestions}
      />
    </>
  );
}

export default SuccessorCard;
